import { Component, OnInit, AfterViewInit } from '@angular/core';

declare const lucide: any;

@Component({
  selector: 'app-samudra',
  templateUrl: './samudra.component.html',
  styleUrls: ['./samudra.component.css']
})
export class SamudraComponent implements OnInit, AfterViewInit {

  // Banner slideshow content
  bannerSlides = [
    {
      id: 0,
      title: "SAMUDRA",
      subtitle: "Smart Automation for Dockyards & Refit Administration",
      description: "Your dockyard's running partner.",
      features: "AI-ready. 100% Indigenous. Secure by design.",
      purpose: "Purpose-built to modernize ship refit and repair operations—end-to-end.",
      backgroundImage: "assets/images/logistics-means-transport-together-with-technological-futuristic-holograms (1).jpg",
      ctaPrimary: "Request a Demo",
      ctaSecondary: "Download SAMUDRA Brochure"
    },
    {
      id: 1,
      title: "SAMUDRA",
      subtitle: "Smart Automation for Dockyards & Refit Administration",
      description: "Your dockyard's running partner.",
      features: "AI-ready. 100% Indigenous. Secure by design.",
      purpose: "Purpose-built to modernize ship refit and repair operations—end-to-end.",
      backgroundImage: "assets/new/logistics.jpg",
      ctaPrimary: "Request a Demo",
      ctaSecondary: "Download SAMUDRA Brochure"
    },
  ];

  // Legacy banner content for backward compatibility
  bannerContent = {
    title: "SAMUDRA — Smart Automation for Maritime Utilities, Dockyards & Refit Administration",
    subtitle: "Your dockyard's running partner.",
    description: "AI-ready. 100% indigenous. Secure by design. Purpose-built to modernize refit, repair, and yard operations—end to end."
  };

  // Problems that shipyards face
  shipyardProblems = [
    {
      icon: 'file-text',
      title: 'Siloed defect reporting and manual work orders',
      description: 'Fragmented systems lead to communication gaps and delayed responses to critical issues.'
    },
    {
      icon: 'clock',
      title: 'Dry-dock delays due to lack of real-time visibility',
      description: 'Poor visibility into operations causes scheduling conflicts and extended downtime.'
    },
    {
      icon: 'users',
      title: 'Scattered manpower, timekeeping, and vendor data',
      description: 'Manual processes create data silos and make workforce management inefficient.'
    },
    {
      icon: 'alert-triangle',
      title: 'Reactive maintenance driving up downtime and costs',
      description: 'Lack of predictive insights leads to unexpected failures and higher maintenance costs.'
    },
    {
      icon: 'shield',
      title: 'Compliance risks from paper-based QA/QC tracking',
      description: 'Manual documentation processes create compliance gaps and audit risks.'
    },
    {
      icon: 'map-pin',
      title: 'Difficulty coordinating berthing, yard services, and multiple sites',
      description: 'Complex logistics across multiple locations without centralized coordination.'
    }
  ];

  // Key features of SAMUDRA
  keyFeatures = [
    {
      icon: 'shield-check',
      title: '100% Indigenous Technology',
      description: 'Built in India for self-reliant defense digitalization with complete data sovereignty.'
    },
    {
      icon: 'lock',
      title: 'Enhanced Cybersecurity',
      description: 'Protects sensitive naval data in connected and air-gapped environments with military-grade security.'
    },
    {
      icon: 'brain',
      title: 'AI-Driven Intelligence',
      description: 'Predictive maintenance for machinery, resource optimization, and intelligent scheduling.'
    },
    {
      icon: 'award',
      title: 'Trusted Track Record',
      description: 'Already deployed in major Asian shipyards and Indian naval dockyards with proven results.'
    }
  ];

  // Built for Maritime & Naval World features
  maritimeFeatures = [
    {
      icon: 'anchor',
      title: 'Maritime-Focused Design',
      description: 'Purpose-built to reflect the unique workflows of refit, repair, and dockyard operations.'
    },
    {
      icon: 'settings',
      title: 'End-to-End Refit Control',
      description: 'A single system to manage everything — from defect logging to dry-dock scheduling, workshop execution, cost tracking, and final sign-off.'
    },
    {
      icon: 'users',
      title: 'Integrated People & Operations',
      description: 'Combines HR, medical, finance, timekeeping, and shift management to remove silos between departments.'
    },
    {
      icon: 'map',
      title: 'Central Oversight Across Yards',
      description: 'Manage multiple shipyards with centralized control while retaining local yard flexibility.'
    },
    {
      icon: 'check-circle',
      title: 'Compliance-Ready by Design',
      description: 'Digital quality inspections, automated compliance documentation, and audit-ready traceability.'
    },
    {
      icon: 'calendar',
      title: 'Smart Berth & Yard Scheduling',
      description: 'Interactive berth/tug planning tools and digital yard-service requests for efficient resource use.'
    }
  ];

  // Command Every Function sections
  commandFunctions = [
    {
      icon: 'wrench',
      title: 'Refit & Repair Management',
      description: 'Plan, monitor, and track entire refit cycles — including defect logging, dry-dock scheduling, workshop coordination, manpower planning, and quality checks.'
    },
    {
      icon: 'building-2',
      title: 'Day-to-Day Yard Operations',
      description: 'Digitize service requests, monitor routine repairs, manage security passes, and gain real-time operational visibility across the dockyard.'
    },
    {
      icon: 'package',
      title: 'Resource & Inventory Control',
      description: 'Centralize spares and materials management, vendor coordination, financial oversight, budget planning, and workforce timekeeping.'
    },
    {
      icon: 'users',
      title: 'Workforce & Administration',
      description: 'Integrated HR, payroll, health records, attendance, and analytics dashboards to keep people, compliance, and policies in sync.'
    }
  ];

  // Built for a Connected Dockyard features
  connectedFeatures = [
    {
      icon: 'link',
      title: 'API-First & Open',
      description: 'Integrates with payment gateways, vendor/customer portals, e-procurement, and government portals.',
      image: 'assets/images/technology-bright-neon-lights.jpg',
      benefits: ['Seamless third-party integrations', 'Real-time data synchronization', 'Reduced manual data entry']
    },
    {
      icon: 'cpu',
      title: 'Machines & IoT',
      description: 'Connects weighing scales, sensors, and shop-floor devices for live condition and consumption data.',
      image: 'assets/images/soldier-using-virtual-tablet-hologram-army-technology (1).jpg',
      benefits: ['Real-time equipment monitoring', 'Predictive maintenance alerts', 'Automated data collection']
    },
    {
      icon: 'smartphone',
      title: 'Mobility-Ready',
      description: 'Mobile app + WhatsApp/Telegram integration for field updates & instant approvals.',
      image: 'assets/images/logistics-means-transport-together-with-technological-futuristic-holograms (1).jpg',
      benefits: ['Field-ready mobile access', 'Offline functionality', 'Real-time updates']
    },
    {
      icon: 'layers',
      title: 'Business Apps-Ready',
      description: 'Works with BI/Analytics, DMS, LMS, Payroll, Salesforce CRM, and more.',
      image: 'assets/images/15727.jpg',
      benefits: ['Legacy system integration', 'Unified data management', 'Streamlined workflows']
    }
  ];

  // Process steps for timeline
  processSteps = [
    {
      title: 'Defect Capture & Reporting',
      description: 'Automated defect logging with photo documentation and priority classification'
    },
    {
      title: 'Work Order Generation',
      description: 'Intelligent work order creation with resource allocation and timeline estimation'
    },
    {
      title: 'Dry-Dock Scheduling',
      description: 'Optimized berth allocation and scheduling with real-time visibility'
    },
    {
      title: 'Resource Management',
      description: 'Comprehensive tracking of manpower, materials, and equipment across all operations'
    },
    {
      title: 'Quality Assurance',
      description: 'Digital QA/QC processes with compliance tracking and audit trails'
    },
    {
      title: 'Financial Control',
      description: 'Integrated cost tracking, vendor management, and financial reporting'
    }
  ];

  // Operational statistics
  operationalStats = [
    {
      icon: 'clock',
      value: '40%',
      label: 'Faster Turnarounds'
    },
    {
      icon: 'trending-down',
      value: '60%',
      label: 'Reduced Downtime'
    },
    {
      icon: 'dollar-sign',
      value: '35%',
      label: 'Cost Savings'
    },
    {
      icon: 'check-circle',
      value: '100%',
      label: 'Compliance Rate'
    }
  ];

  // Lifecycle statistics
  lifecycleStats = [
    {
      icon: 'ship',
      value: '24/7',
      label: 'Vessel Tracking'
    },
    {
      icon: 'users',
      value: '500+',
      label: 'Concurrent Users'
    },
    {
      icon: 'database',
      value: '99.9%',
      label: 'Uptime'
    },
    {
      icon: 'shield',
      value: '100%',
      label: 'Data Security'
    }
  ];

  // The EPPS Advantage
  eppsAdvantages = [
    {
      icon: 'code',
      title: 'Open-Source Core',
      description: 'Java + PostgreSQL: Vendor-neutral, scalable, and future-proof architecture.'
    },
    {
      icon: 'shield',
      title: 'Secure by Design',
      description: 'Role-based access, encryption, and digital audit trails for compliance confidence.'
    },
    {
      icon: 'dollar-sign',
      title: 'Lower TCO',
      description: 'No heavy third-party licensing; deploy on cloud or on-premise with cost efficiency.'
    },
    {
      icon: 'map-pin',
      title: 'Local Expertise',
      description: 'India-first shipyard deployments with fast adoption and responsive support.'
    }
  ];

  // AI-Driven Future features
  aiFeatures = [
    {
      icon: 'brain',
      title: 'Predictive Maintenance & Defect Detection',
      description: 'ML flags failures early to reduce downtime and optimize maintenance schedules.'
    },
    {
      icon: 'calendar',
      title: 'AI-Powered Scheduling & Resource Optimization',
      description: 'Suggests optimal dry-dock slots, manpower allocation, and material use for maximum efficiency.'
    },
    {
      icon: 'message-square',
      title: 'Intelligent Decision Support',
      description: 'NLP chatbot answers asset/SOP queries on demand with contextual intelligence.'
    },
    {
      icon: 'users',
      title: 'Vendor & Workforce Recommendations',
      description: 'ML-based shortlists for performance, skills, and outcomes optimization.'
    },
    {
      icon: 'check-circle',
      title: 'Automated Compliance',
      description: 'AI cross-checks QAM/QCP against updated ISO standards for continuous compliance.'
    },
    {
      icon: 'shield-alert',
      title: 'Cyber Posture Reinforced',
      description: 'AI-aided threat detection for sensitive, air-gapped environments with advanced security.'
    }
  ];

  // Operational Impact
  operationalImpact = [
    {
      icon: 'zap',
      title: 'Faster Turnarounds',
      description: 'Standardized workflows improve refit speed and yard productivity significantly.'
    },
    {
      icon: 'clock',
      title: 'Reduced Downtime',
      description: 'Proactive maintenance and real-time dry-dock planning minimize operational disruptions.'
    },
    {
      icon: 'dollar-sign',
      title: 'Better Cost Control',
      description: 'Accurate tracking of time, materials, and vendor performance for optimized costs.'
    },
    {
      icon: 'shield-check',
      title: 'Assured Compliance',
      description: 'End-to-end traceability with ready-to-audit digital trails for regulatory compliance.'
    }
  ];

  constructor() { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.initializeAnimations();
    this.initializeLucideIcons();
    this.startAutoScroll();
    this.startBannerAutoSlide();
  }

  // Get phase icon based on index
  getPhaseIcon(index: number): string {
    const icons = ['search', 'file-text', 'calendar', 'users', 'shield-check', 'dollar-sign'];
    return icons[index] || 'circle';
  }

  // Get phase details based on index
  getPhaseDetails(index: number): string[] {
    const details = [
      ['Photo documentation', 'Priority classification', 'Automated logging'],
      ['Resource allocation', 'Timeline estimation', 'Work order creation'],
      ['Berth allocation', 'Real-time visibility', 'Optimized scheduling'],
      ['Manpower tracking', 'Material management', 'Equipment monitoring'],
      ['Compliance tracking', 'Audit trails', 'Digital QA/QC'],
      ['Cost tracking', 'Vendor management', 'Financial reporting']
    ];
    return details[index] || [];
  }

  // Banner slideshow functionality
  currentBannerSlide = 0;
  bannerSlideInterval: any;

  // Process steps slider functionality
  currentSlide = 0;
  autoScrollInterval: any;

  nextSlide(): void {
    if (this.currentSlide < this.processSteps.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0; // Restart from 1st slide
    }
  }

  previousSlide(): void {
    if (this.currentSlide > 0) {
      this.currentSlide--;
    } else {
      this.currentSlide = this.processSteps.length - 1; // Go to last slide
    }
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
  }

  startAutoScroll(): void {
    this.autoScrollInterval = setInterval(() => {
      if (this.currentSlide < this.processSteps.length - 1) {
        this.currentSlide++;
      } else {
        this.currentSlide = 0; // Loop back to start
      }
    }, 4000); // Auto-scroll every 4 seconds
  }

  stopAutoScroll(): void {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }

  // Banner slideshow methods
  nextBannerSlide(): void {
    this.currentBannerSlide = (this.currentBannerSlide + 1) % this.bannerSlides.length;
    this.resetBannerAutoSlide();
  }

  previousBannerSlide(): void {
    this.currentBannerSlide = this.currentBannerSlide === 0 ? this.bannerSlides.length - 1 : this.currentBannerSlide - 1;
    this.resetBannerAutoSlide();
  }

  goToBannerSlide(index: number): void {
    this.currentBannerSlide = index;
    this.resetBannerAutoSlide();
  }

  startBannerAutoSlide(): void {
    this.bannerSlideInterval = setInterval(() => {
      this.currentBannerSlide = (this.currentBannerSlide + 1) % this.bannerSlides.length;
    }, 5000); // Auto-slide every 5 seconds
  }

  stopBannerAutoSlide(): void {
    if (this.bannerSlideInterval) {
      clearInterval(this.bannerSlideInterval);
    }
  }

  resetBannerAutoSlide(): void {
    this.stopBannerAutoSlide();
    this.startBannerAutoSlide();
  }

  private initializeAnimations(): void {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  }

  private initializeLucideIcons(): void {
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }
}
